import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSelector } from 'react-redux';

import { useCallback, useEffect, useState, useMemo } from 'react';
import { getUser } from '../../../../redux/usersRedux';
import {
  getApiErrorMessage,
  getMixDeliveries,
} from '../../../../api/mix-deliveriesApi';

import {
  MIX_DELIVERY_STATUS,
  MIX_DELIVERY_STATUS_LABELS,
  MIX_STOCK_STATUS,
  MIX_STOCK_STATUS_LABELS,
} from '../types/mix-deliveries.types';

import MixDeliveryDetailsDialog, {
  DeliveryStatusChip,
  StockStatusChip,
} from './MixDeliveryDetailsDialog';

const DEFAULT_STATUS_FILTERS = [
  MIX_DELIVERY_STATUS.TO_PREPARE,
  MIX_DELIVERY_STATUS.IN_PROGRESS,
];

export default function MixDeliveriesTable({
  refreshKey = 0,
  onDataChanged,
  selectedDeliveryIds = [],
  onSelectedDeliveryIdsChange,
  onTotalDeliveriesChange,
}) {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const user = useSelector((state) => getUser(state));
  const [status, setStatus] = useState(() => [...DEFAULT_STATUS_FILTERS]);
  const [stockStatus, setStockStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getMixDeliveries({
        page: pagination.page,
        limit: pagination.limit,
        statuses:
          Array.isArray(status) && status.length > 0
            ? status.join(',')
            : undefined,
        stockStatus: stockStatus || undefined,
        search: search || undefined,
        onTotalDeliveriesChange,
      });

      setRows(result.data ?? []);

      const totalDeliveries = result.pagination?.total ?? 0;

      onTotalDeliveriesChange?.(totalDeliveries);
      setPagination((current) => ({
        ...current,
        total: totalDeliveries,
        totalPages: result.pagination?.totalPages ?? 0,
      }));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status, stockStatus, search]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries, refreshKey]);

  const handleApplySearch = () => {
    onSelectedDeliveryIdsChange?.([]);

    setPagination((current) => ({
      ...current,
      page: 1,
    }));

    setSearch(searchInput.trim());
  };

  const handleClearFilters = () => {
    onSelectedDeliveryIdsChange?.([]);
    setStatus([]);
    setStockStatus('');
    setSearch('');
    setSearchInput('');

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleStatusChanged = () => {
    loadDeliveries();
    onDataChanged?.();
  };

  const selectedDeliveryIdSet = useMemo(
    () => new Set(selectedDeliveryIds),
    [selectedDeliveryIds],
  );

  const currentPageDeliveryIds = useMemo(
    () => rows.map((delivery) => delivery.id),
    [rows],
  );

  const allCurrentPageSelected =
    currentPageDeliveryIds.length > 0 &&
    currentPageDeliveryIds.every((deliveryId) =>
      selectedDeliveryIdSet.has(deliveryId),
    );

  const someCurrentPageSelected =
    currentPageDeliveryIds.some((deliveryId) =>
      selectedDeliveryIdSet.has(deliveryId),
    ) && !allCurrentPageSelected;

  const handleToggleDelivery = (deliveryId) => {
    const nextSelected = new Set(selectedDeliveryIds);

    if (nextSelected.has(deliveryId)) {
      nextSelected.delete(deliveryId);
    } else {
      nextSelected.add(deliveryId);
    }

    onSelectedDeliveryIdsChange?.([...nextSelected]);
  };

  const handleToggleCurrentPage = () => {
    const nextSelected = new Set(selectedDeliveryIds);

    if (allCurrentPageSelected) {
      currentPageDeliveryIds.forEach((deliveryId) => {
        nextSelected.delete(deliveryId);
      });
    } else {
      currentPageDeliveryIds.forEach((deliveryId) => {
        nextSelected.add(deliveryId);
      });
    }

    onSelectedDeliveryIdsChange?.([...nextSelected]);
  };

  const handleClearSelection = () => {
    onSelectedDeliveryIdsChange?.([]);
  };

  return (
    <Stack spacing={2}>
      <Filters
        status={status}
        stockStatus={stockStatus}
        searchInput={searchInput}
        disabled={loading}
        onStatusChange={(value) => {
          onSelectedDeliveryIdsChange?.([]);
          setStatus(value);

          setPagination((current) => ({
            ...current,
            page: 1,
          }));
        }}
        onStockStatusChange={(value) => {
          onSelectedDeliveryIdsChange?.([]);
          setStockStatus(value);

          setPagination((current) => ({
            ...current,
            page: 1,
          }));
        }}
        onSearchInputChange={setSearchInput}
        onSearch={handleApplySearch}
        onClear={handleClearFilters}
      />

      {selectedDeliveryIds.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
            alignItems={{
              xs: 'stretch',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Wybrano dostawy: {selectedDeliveryIds.length}
                {pagination.total > 0 ? ` z ${pagination.total}` : ''}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Podsumowanie uzupełnień zostanie ograniczone do zaznaczonych
                dostaw.
              </Typography>
            </Box>

            <Button
              size="small"
              variant="outlined"
              onClick={handleClearSelection}
            >
              Wyczyść zaznaczenie
            </Button>
          </Stack>
        </Paper>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  width: 48,
                }}
              >
                <Checkbox
                  checked={allCurrentPageSelected}
                  indeterminate={someCurrentPageSelected}
                  disabled={loading || rows.length === 0}
                  onChange={handleToggleCurrentPage}
                  inputProps={{
                    'aria-label': 'Zaznacz dostawy na aktualnej stronie',
                  }}
                />
              </TableCell>
              <TableCell>Awizacja</TableCell>
              <TableCell>Transport</TableCell>
              <TableCell>Dostawa</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Odbiorca</TableCell>
              <TableCell align="right">Pozycje miksowe</TableCell>
              <TableCell align="right">Łączna ilość</TableCell>
              <TableCell>Kanapkowanie</TableCell>
              <TableCell>Status zapasu</TableCell>
              <TableCell>Status pracy</TableCell>
              <TableCell>Zmiana po przygotowaniu</TableCell>
              <TableCell align="right">Akcja</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((delivery) => (
                <TableRow
                  key={delivery.id}
                  hover
                  sx={{
                    backgroundColor: delivery.requiresReview
                      ? 'rgba(237, 108, 2, 0.06)'
                      : undefined,

                    '&:hover': {
                      backgroundColor: delivery.requiresReview
                        ? 'rgba(237, 108, 2, 0.12)'
                        : undefined,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDeliveryIdSet.has(delivery.id)}
                      onChange={() => handleToggleDelivery(delivery.id)}
                      inputProps={{
                        'aria-label': `Zaznacz dostawę ${delivery.deliveryNumber}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <AppointmentCell value={delivery.loadingAppointmentAt} />
                  </TableCell>

                  <TableCell>
                    {delivery.transportNumber ? (
                      <Typography variant="body2" fontWeight={700}>
                        {delivery.transportNumber}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {delivery.deliveryNumber}
                    </Typography>

                    {delivery.deliveryType && (
                      <Typography variant="caption" color="text.secondary">
                        {delivery.deliveryType}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {formatDate(delivery.deliveryDate ?? delivery.plannedDate)}
                  </TableCell>

                  <TableCell>
                    {delivery.recipientName ?? delivery.recipientNumber ?? '—'}
                  </TableCell>

                  <TableCell align="right">
                    <Chip size="small" label={delivery.mixItemCount} />
                  </TableCell>

                  <TableCell align="right">
                    {formatQuantity(delivery.totalMixQuantity)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={delivery.sandwichingRequired ? 'TAK' : 'NIE'}
                      color={
                        delivery.sandwichingRequired ? 'warning' : 'default'
                      }
                      variant={
                        delivery.sandwichingRequired ? 'filled' : 'outlined'
                      }
                      sx={{
                        fontWeight: delivery.sandwichingRequired ? 700 : 400,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StockStatusChip status={delivery.stockStatus} />
                  </TableCell>

                  <TableCell>
                    <DeliveryStatusChip status={delivery.status} />
                  </TableCell>
                  <TableCell>
                    <DeliveryChangeIndicator delivery={delivery} />
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={
                        delivery.requiresReview ? 'contained' : 'outlined'
                      }
                      color={delivery.requiresReview ? 'warning' : 'primary'}
                      onClick={() => setSelectedDeliveryId(delivery.id)}
                    >
                      {delivery.requiresReview ? 'Sprawdź zmianę' : 'Szczegóły'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                  Brak dostaw spełniających kryteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Wierszy na stronie:"
          onPageChange={(_event, page) => {
            setPagination((current) => ({
              ...current,
              page: page + 1,
            }));
          }}
          onRowsPerPageChange={(event) => {
            setPagination((current) => ({
              ...current,
              page: 1,
              limit: Number(event.target.value),
            }));
          }}
        />
      </TableContainer>

      <MixDeliveryDetailsDialog
        open={Boolean(selectedDeliveryId)}
        deliveryId={selectedDeliveryId}
        onClose={() => setSelectedDeliveryId(null)}
        onStatusChanged={handleStatusChanged}
        userRole={user?.users.role}
      />
    </Stack>
  );
}

function Filters({
  status,
  stockStatus,
  searchInput,
  disabled,
  onStatusChange,
  onStockStatusChange,
  onSearchInputChange,
  onSearch,
  onClear,
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{
          xs: 'column',
          lg: 'row',
        }}
        spacing={2}
        alignItems={{
          xs: 'stretch',
          lg: 'center',
        }}
      >
        <TextField
          size="small"
          label="Szukaj"
          placeholder="Dostawa, materiał lub nazwa"
          value={searchInput}
          disabled={disabled}
          onChange={(event) => onSearchInputChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSearch();
            }
          }}
          sx={{
            minWidth: {
              xs: '100%',
              lg: 300,
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="mix-delivery-status-label" shrink>
            Status pracy
          </InputLabel>

          <Select
            id="mix-delivery-status"
            labelId="mix-delivery-status-label"
            multiple
            displayEmpty
            value={status}
            label="Status pracy"
            disabled={disabled}
            onChange={(event) => {
              const value = event.target.value;

              onStatusChange(
                typeof value === 'string' ? value.split(',') : value,
              );
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <Typography component="span" color="text.secondary">
                    Wszystkie
                  </Typography>
                );
              }

              return selected
                .map((value) => MIX_DELIVERY_STATUS_LABELS[value] ?? value)
                .join(', ');
            }}
          >
            {Object.values(MIX_DELIVERY_STATUS).map((value) => (
              <MenuItem key={value} value={value}>
                <Checkbox checked={status.includes(value)} />

                <ListItemText primary={MIX_DELIVERY_STATUS_LABELS[value]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Status zapasu</InputLabel>

          <Select
            value={stockStatus}
            label="Status zapasu"
            disabled={disabled}
            onChange={(event) => onStockStatusChange(event.target.value)}
          >
            <MenuItem value="">Wszystkie</MenuItem>

            {Object.values(MIX_STOCK_STATUS).map((value) => (
              <MenuItem key={value} value={value}>
                {MIX_STOCK_STATUS_LABELS[value]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            ml: {
              lg: 'auto',
            },
          }}
        >
          <Button variant="contained" disabled={disabled} onClick={onSearch}>
            Szukaj
          </Button>

          <Button variant="outlined" disabled={disabled} onClick={onClear}>
            Wyczyść
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
function DeliveryChangeIndicator({ delivery }) {
  if (!delivery.requiresReview) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip
        size="small"
        color="warning"
        icon={<WarningAmberIcon />}
        label="Wymaga sprawdzenia"
        sx={{
          fontWeight: 700,
        }}
      />

      <Typography variant="caption" color="text.secondary">
        Wersja {delivery.preparedRevisionNumber ?? '—'}
        {' → '}
        {delivery.currentRevisionNumber ?? '—'}
      </Typography>
    </Stack>
  );
}

function AppointmentCell({ value }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const date = new Date(value);

  return (
    <Stack spacing={0}>
      <Typography variant="body2" fontWeight={700}>
        {new Intl.DateTimeFormat('pl-PL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(date)}
      </Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 700,
        }}
      >
        {new Intl.DateTimeFormat('pl-PL', {
          hour: '2-digit',
          minute: '2-digit',
        }).format(date)}
      </Typography>
    </Stack>
  );
}
function formatQuantity(value) {
  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(Number(value ?? 0));
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL').format(new Date(value));
}
